/**
 * features/terms/TermDetail.tsx
 * ------------------------------------------------------------------
 * Per-term detail page — the heart of the unified Terms screen.
 *
 * Five tabs (all scoped to the SAME single term):
 *   1. Status         — open / closed / locked (from old TermManagement)
 *   2. Deadlines      — per-CA + term-final deadlines + month locks
 *                       (Round 4 deadlines + old monthLocks toggles)
 *   3. Assessments    — CA labels for this term (from AssessmentConfigPanel)
 *   4. Exam titles    — per-form exam-column titles (from ExamTitlePanel)
 *   5. Notices        — front/back-page report notices for (year, term)
 *
 * Reached at #/terms/<TermKey>, e.g. #/terms/Term1.
 *
 * Each tab is a focused per-term component in ./tabs/. They share
 * this page's term + year props so navigating between tabs feels
 * like one cohesive workspace, not three different screens.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  ArrowLeft,
  Lock,
  CalendarDays,
  Sliders,
  BookOpen,
  Megaphone,
} from 'lucide-react';
import { Card } from '@/components/ui/primitives';
import { SCHOOL, TermKey, termDates } from '@/lib/school.config';
import StatusTab from './tabs/StatusTab';
import DeadlinesTab from './tabs/DeadlinesTab';
import AssessmentsTab from './tabs/AssessmentsTab';
import ExamTitlesTab from './tabs/ExamTitlesTab';
import NoticesTab from './tabs/NoticesTab';

type DetailTab = 'status' | 'deadlines' | 'assessments' | 'examTitles' | 'notices';

interface Props {
  year: string;
  term: TermKey;
  onBack: () => void;
}

const TABS: { id: DetailTab; label: string; icon: typeof Lock }[] = [
  { id: 'status', label: 'Status', icon: Lock },
  { id: 'deadlines', label: 'Deadlines', icon: CalendarDays },
  { id: 'assessments', label: 'Assessments', icon: Sliders },
  { id: 'examTitles', label: 'Exam titles', icon: BookOpen },
  { id: 'notices', label: 'Notices', icon: Megaphone },
];

export default function TermDetail({ year, term, onBack }: Props) {
  const [tab, setTab] = useState<DetailTab>('status');
  const meta = SCHOOL.terms[term];

  return (
    <div className="max-w-5xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700 dark:hover:text-gray-300"
      >
        <ArrowLeft size={14} /> All terms
      </button>

      {/* Header card */}
      <Card className="p-5 mb-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              {meta.label}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Academic year {year} · {termDates(term)}
            </p>
          </div>
        </div>
      </Card>

      {/* Tab strip */}
      <Card className="p-1.5 mb-4">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
                  active
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Tab content */}
      {tab === 'status' && <StatusTab year={year} term={term} />}
      {tab === 'deadlines' && <DeadlinesTab year={year} term={term} />}
      {tab === 'assessments' && <AssessmentsTab term={term} />}
      {tab === 'examTitles' && <ExamTitlesTab term={term} />}
      {tab === 'notices' && <NoticesTab year={year} term={term} />}
    </div>
  );
}
